<template>
  <v-container class="fill-height pt-8 align-start justify-start">
    <confirmation-modal
      v-if="showLeaveConfirmation"
      action="exit"
      force-icon="exit"
      icon-reversed
      entity-type="Import"
      :force-title="$t('form.import.leaveConfirmation.title')"
      :message="$t('form.import.leaveConfirmation.message')"
      @confirm="confirmLeave"
      @cancel="cancelLeave"
    />
    <confirmation-modal
      v-if="showRemapConfirmation"
      action="continue"
      force-icon="alert"
      entity-type="Import"
      :force-title="$t('form.import.remapConfirmation.title')"
      :message="$t('form.import.remapConfirmation.message')"
      @confirm="confirmRemap"
      @cancel="cancelRemap"
    >
      <template #details>
        <ul class="mt-4 pl-4">
          <li v-for="sheet in sheetsNeedingRemap" :key="sheet">{{ sheet }}</li>
        </ul>
      </template>
    </confirmation-modal>
    <v-row
      :class="{
        'px-8': $vuetify.breakpoint.smAndUp,
        'px-0': $vuetify.breakpoint.xsOnly
      }"
    >
      <v-col>
        <v-stepper v-model="currentStep">
          <v-stepper-header class="flex-nowrap white">
            <template v-for="(step, index) in steps">
              <v-stepper-step
                :key="`biws-${step.step}`"
                :step="step.step"
                :complete="step.completed"
                class="overflow-ellipses"
                @click="jumpTo(step.step)"
                :class="{
                  'cursor-pointer jump-step': canJumpToStep(step.step)
                }"
              >
                {{ step.label }}
              </v-stepper-step>

              <v-divider v-if="index !== steps.length - 1" :key="`biwd-${step.step}`" />
            </template>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content
              v-for="step in steps"
              :key="`biwi-${step.step}`"
              :step="step.step"
              :complete="step.completed"
              class="px-0 pt-1"
            >
              <component
                v-if="step.component && currentStep === step.step"
                :is="step.component"
                v-bind="stepProps"
                ref="stepComponent"
                @next="incrementStep"
                @prev="decrementStep"
                @fileLoaded="handleFileLoaded"
                @sheetsConfigured="handleSheetsConfigured"
                @headerRowChanged="handleHeaderRowChanged"
                @sheetsNeedingRemap="handleSheetsNeedingRemap"
                @mappingsComplete="handleMappingsComplete"
                @importComplete="handleImportComplete"
                @valid="handleValidationChange"
                @companySelected="handleCompanySelected"
                @rowUpdated="handleRowUpdated"
                @rowRemoved="handleRowRemoved"
              />

              <v-row
                v-if="showStepButtons(step)"
                justify="space-between"
                no-gutters
                class="pt-6 px-6 pb-2"
              >
                <v-col class="grow">
                  <platform-button
                    v-if="!step.hidePreviousButton"
                    secondary
                    class="mr-2"
                    :disabled="!canStepBackward"
                    @click="decrementStep"
                  >
                    {{ $t('form.wizard.previous') }}
                  </platform-button>
                </v-col>
                <v-col class="shrink">
                  <platform-button
                    v-if="step.nextButton"
                    primary
                    :disabled="!canStepForward"
                    :loading="loading"
                    @click="incrementStep"
                  >
                    {{ step.nextButton }}
                  </platform-button>
                </v-col>
              </v-row>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <import-history-table
          v-if="currentStep === Steps.Upload"
          :batches="importHistory"
          :loading="historyLoading"
          @viewBatch="handleViewBatch"
        />
      </v-col>
    </v-row>

    <import-history-modal
      v-if="selectedBatch"
      :batch="selectedBatch"
      @close="selectedBatch = null"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import { mapActions } from 'vuex'
import type { WorkBook } from 'xlsx'
import API from '@/services/API'
import { Company } from '@/API'
import {
  BulkImportWizardColumnMapping,
  BulkImportWizardParsedSheet,
  BulkImportWizardRequest,
  BulkImportWizardResponse,
  BulkImportWizardSheetMapping,
  ImportEntityType,
  PlatformImportBatch
} from '@betterboards/shared/types/Import'
import BulkImportUpload from '@/components/import/BulkImportUpload.vue'
import { parseWorksheet } from '@/helpers/import/xlsxParsing'
import BulkImportSheetMapper from '@/components/import/BulkImportSheetMapper.vue'
import BulkImportFieldMapper from '@/components/import/BulkImportFieldMapper.vue'
import BulkImportPreview from '@/components/import/BulkImportPreview.vue'
import BulkImportResults from '@/components/import/BulkImportResults.vue'
import ImportHistoryTable from '@/components/import/ImportHistoryTable.vue'
import ImportHistoryModal from '@/components/import/ImportHistoryModal.vue'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import { NavigationGuardNext, Route } from 'vue-router'
import { applyMappings } from '@/helpers/import/fieldMapper'
import { getImportHistory } from '@/helpers/import/importHistory'
import { normalizeCommitteeSheet, normalizeMultiColumnCommitteeSheet, detectCommitteeSheetFormat, CommitteeSheetFormat } from '@/helpers/import/normalizeCommitteeSheet'

enum Steps {
  Upload = 1,
  SheetSelection = 2,
  FieldMapping = 3,
  Preview = 4,
  Results = 5
}

interface BulkImportWizardStep {
  label: string
  step: Steps
  completed: boolean
  component?: typeof Vue
  nextButton?: string
  hidePreviousButton?: boolean
}

export default Vue.extend({
  name: 'BulkImportWizard',
  components: {
    BulkImportUpload,
    BulkImportSheetMapper,
    BulkImportFieldMapper,
    BulkImportPreview,
    BulkImportResults,
    ImportHistoryTable,
    ImportHistoryModal,
    ConfirmationModal
  },
  data: () => ({
    currentStep: 1,
    loading: false,
    parsedSheets: [] as BulkImportWizardParsedSheet[],
    sheetMappings: [] as BulkImportWizardSheetMapping[],
    importResults: null as BulkImportWizardResponse | null,
    fieldMappingValid: false,
    previewValid: false,
    targetCompanyId: null as string | null,
    showLeaveConfirmation: false,
    showRemapConfirmation: false,
    sheetsNeedingRemap: [] as string[],
    pendingNavigation: null as (() => void) | null,
    importHistory: [] as PlatformImportBatch[],
    historyLoading: false,
    selectedBatch: null as PlatformImportBatch | null,
    workbook: null as WorkBook | null,
    Steps
  }),
  computed: {
    selectedCompany (): Company {
      return this.$store.state.Company.selectedCompany
    },
    effectiveCompanyId (): string {
      return this.targetCompanyId || this.selectedCompany?.id
    },
    steps (): BulkImportWizardStep[] {
      return [
        {
          label: this.$t('form.import.steps.upload') as string,
          step: Steps.Upload,
          component: BulkImportUpload,
          completed: this.parsedSheets.length > 0,
          hidePreviousButton: true
        },
        {
          label: this.$t('form.import.steps.selectSheets') as string,
          step: Steps.SheetSelection,
          component: BulkImportSheetMapper,
          nextButton: this.$t('form.import.buttons.continue') as string,
          completed: this.sheetMappings.some(m => m.entityType !== null)
        },
        {
          label: this.$t('form.import.steps.mapFields') as string,
          step: Steps.FieldMapping,
          component: BulkImportFieldMapper,
          nextButton: this.$t('form.import.buttons.preview') as string,
          completed: this.fieldMappingValid
        },
        {
          label: this.$t('form.import.steps.preview') as string,
          step: Steps.Preview,
          component: BulkImportPreview,
          nextButton: this.$t('form.import.buttons.import') as string,
          completed: this.previewValid
        },
        {
          label: this.$t('form.import.steps.results') as string,
          step: Steps.Results,
          component: BulkImportResults,
          completed: false,
          hidePreviousButton: true
        }
      ]
    },
    currentStepIndex (): number {
      return this.steps.findIndex((s) => s.step === this.currentStep)
    },
    canStepForward (): boolean {
      const step = this.steps[this.currentStepIndex]
      if (!step) {
        return false
      }
      return step.completed || this.currentStep < Steps.Results
    },
    canStepBackward (): boolean {
      return this.currentStep > Steps.Upload && this.currentStep < Steps.Results
    },
    stepProps (): Record<string, unknown> {
      return {
        parsedSheets: this.parsedSheets,
        sheetMappings: this.sheetMappings,
        importResults: this.importResults,
        companyId: this.selectedCompany?.id,
        selectedCompanyId: this.effectiveCompanyId,
        loading: this.loading
      }
    },
    hasImportProgress (): boolean {
      return this.currentStep > Steps.Upload && this.currentStep < Steps.Results
    }
  },
  watch: {
    effectiveCompanyId: {
      immediate: true,
      handler (newId: string): void {
        if (newId) {
          this.fetchImportHistory()
        }
      }
    }
  },
  beforeRouteLeave (to: Route, from: Route, next: NavigationGuardNext): void {
    if (this.hasImportProgress && !this.showLeaveConfirmation) {
      this.showLeaveConfirmation = true
      this.pendingNavigation = () => next()
      next(false)
    } else {
      next()
    }
  },
  methods: {
    ...mapActions('Company', ['selectCompany']),
    async fetchImportHistory (): Promise<void> {
      if (!this.effectiveCompanyId) {
        return
      }
      this.historyLoading = true
      try {
        this.importHistory = await getImportHistory(this.effectiveCompanyId)
      } catch (err) {
        console.error('Failed to fetch import history:', err)
        this.importHistory = []
      } finally {
        this.historyLoading = false
      }
    },
    handleViewBatch (batch: PlatformImportBatch): void {
      this.selectedBatch = batch
    },
    handleFileLoaded (payload: { sheets: BulkImportWizardParsedSheet[], workbook: WorkBook, detectedHeaderRows: Record<string, number> }): void {
      this.parsedSheets = payload.sheets
      this.workbook = payload.workbook
      this.sheetMappings = payload.sheets.map(sheet => ({
        sheetName: sheet.name,
        entityType: null,
        fieldMappings: [],
        headerRow: payload.detectedHeaderRows[sheet.name] ?? 0
      }))
      this.incrementStep()
    },
    handleHeaderRowChanged (payload: { sheetName: string, headerRow: number }): void {
      if (!this.workbook) {
        return
      }
      const sheetIndex = this.parsedSheets.findIndex(s => s.name === payload.sheetName)
      if (sheetIndex === -1) {
        return
      }
      const reparsedSheet = parseWorksheet(this.workbook, payload.sheetName, payload.headerRow)
      this.parsedSheets = [
        ...this.parsedSheets.slice(0, sheetIndex),
        reparsedSheet,
        ...this.parsedSheets.slice(sheetIndex + 1)
      ]
    },
    handleSheetsConfigured (mappings: BulkImportWizardSheetMapping[]): void {
      this.sheetMappings = mappings
    },
    handleSheetsNeedingRemap (sheets: string[]): void {
      this.sheetsNeedingRemap = sheets
    },
    handleMappingsComplete (mappings: BulkImportWizardSheetMapping[]): void {
      this.sheetMappings = mappings
    },
    handleValidationChange (isValid: boolean): void {
      if (this.currentStep === Steps.FieldMapping) {
        this.fieldMappingValid = isValid
      } else if (this.currentStep === Steps.Preview) {
        this.previewValid = isValid
      }
    },
    handleImportComplete (results: BulkImportWizardResponse): void {
      this.importResults = results
      this.currentStep = Steps.Results
    },
    handleCompanySelected (companyId: string): void {
      this.targetCompanyId = companyId
    },
    handleRowUpdated (payload: { sheetName: string, rowIndex: number, updatedData: Record<string, unknown> }): void {
      const sheetIndex = this.parsedSheets.findIndex(s => s.name === payload.sheetName)
      if (sheetIndex !== -1) {
        const sheet = this.parsedSheets[sheetIndex]
        if (sheet.rows[payload.rowIndex]) {
          Object.assign(sheet.rows[payload.rowIndex], payload.updatedData)
          this.parsedSheets = [...this.parsedSheets]
        }
      }
    },
    handleRowRemoved (payload: { sheetName: string, rowIndex: number }): void {
      const sheetIndex = this.parsedSheets.findIndex(s => s.name === payload.sheetName)
      if (sheetIndex !== -1) {
        const sheet = this.parsedSheets[sheetIndex]
        sheet.rows.splice(payload.rowIndex, 1)
        this.parsedSheets = [...this.parsedSheets]
      }
    },
    async incrementStep (): Promise<void> {
      if (!this.canStepForward) {
        return
      }

      if (this.currentStep === Steps.SheetSelection && this.sheetsNeedingRemap.length > 0) {
        this.showRemapConfirmation = true
        return
      }

      if (this.currentStep === Steps.Preview) {
        await this.executeImport()
        return
      }

      this.steps[this.currentStepIndex].completed = true
      this.currentStep += 1
    },
    getSourceColumnForField (mappings: BulkImportWizardColumnMapping[], targetField: string): string | undefined {
      const mapping: BulkImportWizardColumnMapping | undefined = mappings.find((mapping) => mapping.targetField === targetField)
      return mapping?.sourceColumn
    },
    getNormalizedRows (sheet: BulkImportWizardParsedSheet, mapping: BulkImportWizardSheetMapping): Array<Record<string, unknown>> {
      if (mapping.entityType !== 'Committee') {
        return sheet.rows
      }

      const nameColumn: string | undefined = this.getSourceColumnForField(mapping.fieldMappings, 'name')
      const memberColumn: string | undefined = this.getSourceColumnForField(mapping.fieldMappings, 'members')

      if (!nameColumn || !memberColumn) {
        return sheet.rows
      }

      const format = detectCommitteeSheetFormat(sheet.rows, nameColumn, memberColumn, sheet.columns)

      if (format === CommitteeSheetFormat.MultiColumn) {
        return normalizeMultiColumnCommitteeSheet(sheet.rows, nameColumn, memberColumn, sheet.columns)
      }

      if (format === CommitteeSheetFormat.OnePerRow) {
        return normalizeCommitteeSheet(sheet.rows, nameColumn, memberColumn)
      }

      return sheet.rows
    },
    isMappedRowEmpty (row: Record<string, unknown>, entityType: ImportEntityType): boolean {
      const dataKeys = Object.keys(row).filter(k => k !== 'isValid' && k !== 'missingFields')
      const hasData = dataKeys.some(key => {
        const value = row[key]
        if (value === undefined || value === null) {
          return false
        }
        if (Array.isArray(value)) {
          return value.length > 0
        }
        return String(value).trim() !== ''
      })

      if (!hasData) {
        return true
      }

      // For committees, must have a name to be valid
      if (entityType === 'Committee') {
        const name = row.name
        return !name || String(name).trim() === ''
      }

      return false
    },
    getFilteredRows (sheet: BulkImportWizardParsedSheet, mapping: BulkImportWizardSheetMapping): Array<Record<string, unknown>> {
      const normalizedRows = this.getNormalizedRows(sheet, mapping)
      const entityType = mapping.entityType as ImportEntityType

      return normalizedRows
        .map(row => applyMappings(row, mapping.fieldMappings, entityType))
        .filter(mappedRow => !this.isMappedRowEmpty(mappedRow, entityType))
    },
    async executeImport (): Promise<void> {
      this.loading = true

      try {
        const activeMappings = this.sheetMappings.filter(m => m.entityType !== null)
        const request: BulkImportWizardRequest = {
          companyId: this.effectiveCompanyId,
          sheets: activeMappings.map(mapping => {
            const sheet = this.parsedSheets.find(s => s.name === mapping.sheetName)
            const filteredRows = sheet ? this.getFilteredRows(sheet, mapping) : []
            return {
              entityType: mapping.entityType!,
              teamId: mapping.teamId,
              fieldMappings: mapping.fieldMappings,
              rows: filteredRows
            }
          })
        }

        const result: BulkImportWizardResponse = await API.post('backendfunctions', '/company/import', {
          body: request
        })

        const createdCompany = result.created.company?.[0]
        if (createdCompany?.id) {
          try {
            await this.selectCompany(createdCompany as Company)
            this.targetCompanyId = createdCompany.id
          } catch (err) {
            console.error('Failed to switch to newly created company:', err)
          }
        }

        this.importResults = result
        this.steps[this.currentStepIndex].completed = true
        this.currentStep = Steps.Results
      } catch (err) {
        console.error('Import failed:', err)
        this.$toasted.error(this.$t('form.import.errors.importFailed') as string)
      } finally {
        this.loading = false
      }
    },
    decrementStep (): void {
      if (!this.canStepBackward) {
        return
      }
      this.currentStep -= 1
    },
    canJumpToStep (step: Steps): boolean {
      const index = this.steps.findIndex((s) => s.step === step)
      if (!this.steps[index]) {
        return false
      }
      if (step < this.currentStep) {
        return true
      }
      if (this.steps[index - 1] && this.steps[index - 1].completed) {
        return true
      }
      return this.steps[index].completed
    },
    jumpTo (step: Steps): void {
      if (!this.canJumpToStep(step)) {
        return
      }
      if (
        this.currentStep === Steps.SheetSelection &&
        step >= Steps.FieldMapping &&
        this.sheetsNeedingRemap.length > 0
      ) {
        this.showRemapConfirmation = true
        return
      }
      this.currentStep = step
    },
    showStepButtons (step: BulkImportWizardStep): boolean {
      if (step.step === Steps.Results) {
        return false
      }
      if (step.step === Steps.Upload) {
        return false
      }
      return true
    },
    confirmLeave (): void {
      this.showLeaveConfirmation = false
      if (this.pendingNavigation) {
        this.pendingNavigation()
        this.pendingNavigation = null
      }
    },
    cancelLeave (): void {
      this.showLeaveConfirmation = false
      this.pendingNavigation = null
    },
    confirmRemap (): void {
      this.sheetMappings = this.sheetMappings.map(mapping => {
        if (this.sheetsNeedingRemap.includes(mapping.sheetName)) {
          return {
            ...mapping,
            fieldMappings: []
          }
        }
        return mapping
      })

      this.fieldMappingValid = false
      this.previewValid = false
      this.sheetsNeedingRemap = []
      this.showRemapConfirmation = false

      this.steps[this.currentStepIndex].completed = true
      this.currentStep = Steps.FieldMapping
    },
    cancelRemap (): void {
      this.showRemapConfirmation = false
    }
  }
})
</script>

<style lang="scss" scoped>
.jump-step {
  &:hover {
    opacity: 0.7;
  }
}
</style>
